import { useEffect, useState } from 'react';
import { ConditionalNavigation } from '../ConditionalNavigation';
import {
  getValidationErrorMessages,
  getHasAffordableChanged,
  getHasValidationErrors,
} from './utils';
import { CONFIRMATION_MODES } from './constants';

export const useAffordable = ({ affordable, saveAffordable }) => {
  const {
    details: affordableDetails = '',
    income_eligibility: affordableIncomeEligibility = [],
  } = affordable;

  const [confirmationMode, setConfirmationMode] = useState(null);
  const [details, setDetails] = useState(affordableDetails);
  const [detailsValidationErrorMessage, setDetailsValidationErrorMessage] =
    useState('');
  const [incomeEligibility, setIncomeEligibility] = useState(
    affordableIncomeEligibility
  );

  useEffect(() => {
    ConditionalNavigation.setOpenNavigationWarning(() => {
      setConfirmationMode(CONFIRMATION_MODES.UNSAVED);
    });

    return () => {
      ConditionalNavigation.resetConditionalNavigation();
    };
  }, []);

  const hasAffordableChanged = getHasAffordableChanged({
    affordableDetails,
    affordableIncomeEligibility,
    details,
    incomeEligibility,
  });

  ConditionalNavigation.setShouldUseConditionalNavigation(hasAffordableChanged);

  const deleteEligibleQualification = (maximum_income, occupants) => {
    setIncomeEligibility((prev) => {
      const updatedIncomeEligibility = prev.filter(
        (eligibleQualification) =>
          eligibleQualification.maximum_income !== maximum_income ||
          eligibleQualification.occupants !== occupants
      );
      return updatedIncomeEligibility;
    });
  };

  const handleSaveAffordable = async () => {
    const transformedIncomeEligibility = incomeEligibility.map(
      (eligibleQualification) => ({
        maximum_income: eligibleQualification.maximum_income,
        occupants: eligibleQualification.occupants,
      })
    );

    try {
      await saveAffordable({
        details,
        incomeEligibility: transformedIncomeEligibility,
      });
    } catch {
      return;
    }
  };

  const handleUpdateOnClick = () => {
    const errorMessages = getValidationErrorMessages(
      details,
      incomeEligibility
    );

    if (errorMessages) {
      const { details, incomeEligibility: updatedIncomeEligibility } =
        errorMessages;

      if (details) {
        setDetailsValidationErrorMessage(details);
      }

      if (updatedIncomeEligibility) {
        setIncomeEligibility(updatedIncomeEligibility);
      }
      return;
    }

    setConfirmationMode(CONFIRMATION_MODES.UPDATE);
  };

  const handleConfirmation = () => {
    setConfirmationMode(null);

    if (confirmationMode === CONFIRMATION_MODES.UPDATE) {
      handleSaveAffordable();
    }

    if (confirmationMode === CONFIRMATION_MODES.UNSAVED) {
      handleUpdateOnClick();
    }
  };

  const updateEligibleQualification =
    (maximum_income, occupants) => (updates) => {
      setIncomeEligibility((prevState) =>
        prevState.map((eligibleQualification) => {
          if (
            eligibleQualification.maximum_income === maximum_income &&
            eligibleQualification.occupants === occupants
          ) {
            return {
              ...eligibleQualification,
              ...updates,
            };
          }
          return eligibleQualification;
        })
      );
    };

  const clearIncomeEligibilityValidationErrorMessage =
    (maximum_income, occupants) => (fieldName) => {
      setIncomeEligibility((prevState) =>
        prevState.map((eligibleQualification) => {
          if (
            eligibleQualification.maximum_income === maximum_income &&
            eligibleQualification.occupants === occupants
          ) {
            return {
              ...eligibleQualification,
              errorMessages: {
                ...eligibleQualification.errorMessages,
                [fieldName]: '',
              },
            };
          }

          return eligibleQualification;
        })
      );
    };

  const closeConfirmationDialog = (_event, reason) => {
    const shouldNotCloseDialog =
      confirmationMode === CONFIRMATION_MODES.UNSAVED &&
      (reason === 'backdropClick' || reason === 'escapeKeyDown');

    if (shouldNotCloseDialog) {
      return;
    }
    setConfirmationMode(null);
  };

  const handleResumeNavigation = () => {
    ConditionalNavigation.resumeNavigation();
    setConfirmationMode(null);
    setDetails(affordableDetails);
    setIncomeEligibility(affordableIncomeEligibility);
  };

  const getConfirmationSecondaryButtonAction = () => {
    if (confirmationMode === CONFIRMATION_MODES.UNSAVED) {
      return handleResumeNavigation;
    }
  };

  const hasValidationErrors = getHasValidationErrors(
    detailsValidationErrorMessage,
    incomeEligibility
  );

  const isUpdateDisabled = !hasAffordableChanged || hasValidationErrors;

  return {
    clearIncomeEligibilityValidationErrorMessage,
    closeConfirmationDialog,
    confirmationMode,
    confirmationSecondaryButtonAction: getConfirmationSecondaryButtonAction(),
    deleteEligibleQualification,
    details,
    detailsValidationErrorMessage,
    handleConfirmation,
    handleUpdateOnClick,
    incomeEligibility,
    isUpdateDisabled,
    setConfirmationMode,
    setDetails,
    setDetailsValidationErrorMessage,
    setIncomeEligibility,
    updateEligibleQualification,
  };
};
